/**
 * ElencoInterventi — Scheda V3
 *
 * Mostra gli interventi di una scheda di lavoro come schede leggibili,
 * con cambio stato inline, dettagli collassabili e assegnazione officina.
 * Supporta creazione, modifica, cancellazione e fotografia per intervento.
 *
 * NON importa nulla da scheda-v2.
 */
import React, { useState, useCallback, useMemo } from 'react'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Descriptions,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd'
import {
  ApartmentOutlined,
  CameraOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SafetyOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import type { WorkOrder, Intervention, InterventionStatusType } from '@/types'
import { useInterventionStatusTypes, useInterventionResolutionModes } from '@/hooks/useSystemTables'
import { usePermission } from '@/hooks/usePermission'
import axiosInstance from '@/lib/axios'
import AssegnaOfficinaModal from '@/components/AssegnaOfficinaModal'
import { useAuth } from '@/contexts/AuthContext'
import NuovoInterventoModal, { type NuovoInterventoData } from '@/components/NuovoInterventoModal'
import RdaInterventoModal from '@/components/RdaInterventoModal'
import dayjs from 'dayjs'
import { useRdasForWorkOrder } from '@/hooks/useRdaIntervento'
import BloccoRdaIntervento from '@/components/shared/BloccoRdaIntervento'
import { useWorkshopQuotes } from '@/hooks/useWorkshopQuotes'
import type { WorkshopQuote } from '@/types/workshopQuotes'
import BloccoPreventiviOfficina from '@/components/shared/BloccoPreventiviOfficina'

const { Text } = Typography

// ── Colore bordo sinistro per stato ──────────────────────────────────────────
// Derivato dai token Ant Design: non usiamo hex fissi.
// La funzione riceve i token e il codice stato e restituisce il colore.
function statoColor(codice: string, token: ReturnType<typeof theme.useToken>['token']): string {
  switch (codice) {
    case 'non_iniziato': return token.colorBorderSecondary
    case 'iniziato':     return token.colorPrimary
    case 'sospeso':      return token.colorWarning
    case 'completato':   return token.colorSuccess
    default:             return token.colorBorderSecondary
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props {
  workOrder: WorkOrder
  onChange: () => void
  /** Apre acquisizione foto con fase='lavorazione' e intervento pre-selezionato.
   *  Se omessa, il pulsante fotocamera non compare. */
  onPhotoCaptureIntervention?: (interventionId: number) => void
}

// ── Modale nota obbligatoria ──────────────────────────────────────────────────
interface NotaModal {
  interventionId: number
  statoId: number
}

// Consenso avvertito (§5): riprendere un intervento fermo per una RDA ancora
// pendente. La modale nomina la RDA e chiede una motivazione obbligatoria.
interface ConsensoModal {
  interventionId: number
  statoId: number
  rdaCodice: string
  rdaFornitore: string | null
}

// ── Modale assegna officina ───────────────────────────────────────────────────
interface AssegnaModal {
  interventionId: number
  currentWorkshopId: number | null
}

// ── Modale RDA post-creazione ─────────────────────────────────────────────────
interface RdaAfterNew {
  open: boolean
  interventionId: number
  descrizione: string
  note: string | null
}

// ── Componente singola scheda intervento ──────────────────────────────────────
interface CardInterventoProps {
  intervention: Intervention
  workOrderId: number
  interventionStatuses: InterventionStatusType[]
  resolutionLabels: Record<number, string>
  onSaveStato: (interventionId: number, statoId: number, nota?: string) => Promise<void>
  onSaveOre: (interventionId: number, ore: number | null) => Promise<void>
  onAssegnaClick: (interventionId: number, currentWorkshopId: number | null) => void
  onEdit: (intervention: Intervention) => void
  onDelete: (interventionId: number) => void
  onPhotoCaptureIntervention?: (interventionId: number) => void
  savingStatoId: number | null
  savingOreId: number | null
  deletingId: number | null
  canEditOre: boolean
  /** work_orders.edit OR interventions.update_status, come canEditStato in V2 */
  canEditStato: boolean
  canEdit: boolean
  /** interventions.delete, escluse le officine esterne (stessa regola della V2) */
  canDelete: boolean
  canCreatePhotos: boolean
  /** workshop_quotes.request, escluse le officine esterne (stessa regola della V2) */
  canAssign: boolean
  // ── Copertura sinistro ──────────────────────────────────────────────────────
  /** La scheda ha un sinistro registrato: senza sinistro il campo non ha senso */
  hasSinistro: boolean
  numeroSinistro?: string | null
  /** Modificabile solo da AMMINISTRAZIONE/DIREZIONE (work_orders.edit) */
  canEditSinistro: boolean
  onSaveSinistro: (interventionId: number, coperto: boolean) => Promise<void>
  savingSinistroId: number | null
  // ── Preventivi officina per BloccoPreventiviOfficina ────────────────────────
  /** Quote già filtrate per questo intervento */
  quotes: WorkshopQuote[]
  canViewQuotes: boolean
  canApprove: boolean
  // ── Dati RDA/RDO per BloccoRdaIntervento ────────────────────────────────────
  interventionRdaMap: Record<number, { id: number; note?: string | null; items: { id: number; descrizione: string }[] }>
  rdaChecks: Record<number, Record<number, boolean>>
  rdaDates: Record<number, Record<number, ReturnType<typeof dayjs> | null>>
  savingRda: Record<number, boolean>
  onToggleItemCheck: (prId: number, itemId: number, checked: boolean) => void
  onDateChange: (prId: number, itemId: number, date: ReturnType<typeof dayjs> | null) => void
  onSaveRdaReceive: (pr: any) => void
}

const CardIntervento: React.FC<CardInterventoProps> = ({
  intervention,
  workOrderId,
  interventionStatuses,
  resolutionLabels,
  onSaveStato,
  onSaveOre,
  onAssegnaClick,
  onEdit,
  onDelete,
  onPhotoCaptureIntervention,
  savingStatoId,
  savingOreId,
  deletingId,
  canEditOre,
  canEditStato,
  canEdit,
  canDelete,
  canCreatePhotos,
  canAssign,
  hasSinistro,
  numeroSinistro,
  canEditSinistro,
  onSaveSinistro,
  savingSinistroId,
  quotes,
  canViewQuotes,
  canApprove,
  interventionRdaMap,
  rdaChecks,
  rdaDates,
  savingRda,
  onToggleItemCheck,
  onDateChange,
  onSaveRdaReceive,
}) => {
  const { token } = theme.useToken()
  const [open, setOpen] = useState(false)
  const [oreLocal, setOreLocal] = useState<number | null>(intervention.ore_effettive ?? null)

  const statoAttuale = interventionStatuses.find(s => s.id === intervention.stato_intervento_id)
  const borderColor = statoColor(statoAttuale?.codice ?? '', token)

  const isSavingStato  = savingStatoId === intervention.id
  const isSavingOre    = savingOreId === intervention.id
  const isDeleting     = deletingId === intervention.id
  const isSavingSinistro = savingSinistroId === intervention.id

  // Scarto ore effettive vs stima — solo se la stima è significativa (> 0)
  const stimaValida = intervention.durata_stimata > 0
  const scarto = (oreLocal != null && stimaValida) ? oreLocal - intervention.durata_stimata : null

  const scartoEl = scarto == null ? null : scarto === 0 ? (
    <Text type="secondary" style={{ fontSize: 11 }}>in linea con la stima</Text>
  ) : (
    <Text style={{ fontSize: 11, color: scarto > 0 ? token.colorError : token.colorSuccess }}>
      {scarto > 0 ? '+' : ''}{scarto.toFixed(1)} h rispetto alla stima
    </Text>
  )

  const handleOreBlur = useCallback(() => {
    const prev = intervention.ore_effettive ?? null
    if (oreLocal !== prev) {
      onSaveOre(intervention.id!, oreLocal)
    }
  }, [oreLocal, intervention.ore_effettive, intervention.id, onSaveOre])

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: token.borderRadius,
        padding: '12px 16px',
        marginBottom: 8,
        transition: 'border-left-color 0.2s',
      }}
    >
      {/* ── Riga superiore: titolo + select stato + azioni ─── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        {/* Titolo */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ color: token.colorText, fontSize: 14 }}>
            {intervention.progressivo != null ? `${intervention.progressivo} · ` : ''}
            {intervention.descrizione_intervento}
          </Text>
        </div>

        {/* Select stato */}
        <Spin spinning={isSavingStato} size="small">
          <Select
            size="small"
            style={{ width: 148, flexShrink: 0 }}
            value={intervention.stato_intervento_id ?? undefined}
            placeholder="Non iniziato"
            disabled={!canEditStato || isSavingStato}
            onChange={(val: number) => onSaveStato(intervention.id!, val)}
            options={interventionStatuses.map(s => ({ value: s.id, label: s.nome }))}
          />
        </Spin>

        {/* Azioni: fotocamera · modifica · elimina */}
        <Space size={4}>
          {onPhotoCaptureIntervention && canCreatePhotos && intervention.id != null && (
            <Tooltip title="Fotografa questo intervento">
              <Button
                size="small"
                icon={<CameraOutlined />}
                onClick={() => onPhotoCaptureIntervention(intervention.id!)}
              />
            </Tooltip>
          )}
          {canEdit && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(intervention)}
            />
          )}
          {canDelete && (
            <Popconfirm
              title="Eliminare questo intervento?"
              onConfirm={() => onDelete(intervention.id!)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={isDeleting}
              />
            </Popconfirm>
          )}
        </Space>
      </div>

      {/* ── Note sospensione (visibili solo se sospeso) ─── */}
      {statoAttuale?.codice === 'sospeso' && intervention.note_sospensione && (
        <div style={{
          marginTop: 6,
          padding: '6px 10px',
          background: token.colorWarningBg,
          borderRadius: token.borderRadiusSM,
          borderLeft: `2px solid ${token.colorWarning}`,
        }}>
          <Text style={{ fontSize: 12, color: token.colorWarningText }}>
            Sospeso: {intervention.note_sospensione}
          </Text>
        </div>
      )}

      {/* ── Riga meta ─── */}
      <Space size={4} wrap style={{ marginTop: 8 }}>
        {/* Modalità risoluzione — label dalla lookup, non cablata */}
        {intervention.modalita_risoluzione_id != null && (
          <Tag color={intervention.modalita_risoluzione_id === 2 ? 'purple' : 'cyan'} style={{ margin: 0 }}>
            {resolutionLabels[intervention.modalita_risoluzione_id] ?? `Mod. ${intervention.modalita_risoluzione_id}`}
          </Tag>
        )}

        {/* Tipo intervento */}
        <Tag color={intervention.tipo_intervento === 'Meccanico' ? 'blue' : 'orange'} style={{ margin: 0 }}>
          {intervention.tipo_intervento}
        </Tag>

        {/* Durata stimata: valore reale oppure avviso stima mancante */}
        {intervention.durata_stimata > 0 ? (
          <Tag style={{ margin: 0 }} icon={<ToolOutlined />}>
            {intervention.durata_stimata} h stimate
          </Tag>
        ) : (
          <Tag color="warning" style={{ margin: 0 }} icon={<ToolOutlined />}>
            stima mancante
          </Tag>
        )}

        {/* Coperto dal sinistro */}
        {intervention.coperto_sinistro && (
          <Tag color="red" style={{ margin: 0 }}>coperto dal sinistro</Tag>
        )}

        {/* Officina — stessa logica V2 (FaseAnalisi colonna Officina):
              quota aperta/approvata → nome statico non cliccabile */}
        {(() => {
          const workshopNome = (intervention as any).workshop_nome
          const relevantQuote = quotes.find(q =>
            ['inviata', 'sopralluogo_richiesto', 'risposta_ricevuta', 'approvata'].includes(q.stato)
          )
          if (relevantQuote) {
            return (
              <Tag
                color="orange"
                icon={<ApartmentOutlined />}
                style={{ margin: 0 }}
              >
                {relevantQuote.workshop_nome ?? workshopNome ?? '—'}
              </Tag>
            )
          }
          // Come in V2 (FaseAnalisi, colonna Officina): senza il permesso il
          // nome dell'officina gia' assegnata resta visibile ma non cliccabile,
          // mentre l'invito ad assegnare non compare affatto.
          if (workshopNome) {
            return (
              <Tooltip title={canAssign ? 'Cambia officina' : undefined}>
                <Tag
                  color="orange"
                  icon={<ApartmentOutlined />}
                  style={{ margin: 0, cursor: canAssign ? 'pointer' : 'default' }}
                  onClick={
                    canAssign
                      ? () => onAssegnaClick(intervention.id!, (intervention as any).workshop_id ?? null)
                      : undefined
                  }
                >
                  {workshopNome}
                </Tag>
              </Tooltip>
            )
          }
          if (!canAssign) return null
          return (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<ApartmentOutlined />}
              onClick={() => onAssegnaClick(intervention.id!, null)}
            >
              Assegna officina
            </Button>
          )
        })()}
      </Space>

      {/* ── Pannello Dettagli collassabile ─── */}
      <div style={{ marginTop: 10 }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: token.colorTextSecondary,
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
          Dettagli
        </button>

        {open && (
          <div style={{
            marginTop: 8,
            padding: '10px 12px',
            background: token.colorBgLayout,
            borderRadius: token.borderRadiusSM,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}>
            <Descriptions
              column={1}
              size="small"
              styles={{ label: { color: token.colorTextSecondary, fontWeight: 500, width: 160 } }}
            >
              <Descriptions.Item label="Descrizione">
                <Text style={{ color: token.colorText }}>
                  {intervention.descrizione_intervento}
                </Text>
              </Descriptions.Item>

              {intervention.note_intervento && (
                <Descriptions.Item label="Note">
                  <Text style={{ color: token.colorText }}>{intervention.note_intervento}</Text>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Modalità">
                <Text style={{ color: token.colorText }}>
                  {intervention.modalita_risoluzione_id != null
                    ? resolutionLabels[intervention.modalita_risoluzione_id] ?? '—'
                    : '—'}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Tipo">
                <Text style={{ color: token.colorText }}>{intervention.tipo_intervento}</Text>
              </Descriptions.Item>

              <Descriptions.Item label="Durata stimata">
                {intervention.durata_stimata > 0 ? (
                  <Text style={{ color: token.colorText }}>{intervention.durata_stimata} h</Text>
                ) : (
                  <Text style={{ color: token.colorWarning }}>stima mancante</Text>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Ore effettive">
                {canEditOre ? (
                  <Space size={8} align="center">
                    <Spin spinning={isSavingOre} size="small">
                      <InputNumber
                        size="small"
                        min={0}
                        max={9999}
                        step={0.5}
                        value={oreLocal ?? undefined}
                        placeholder="—"
                        style={{ width: 90 }}
                        onChange={val => setOreLocal(val ?? null)}
                        onBlur={handleOreBlur}
                        onPressEnter={handleOreBlur}
                      />
                    </Spin>
                    {scartoEl}
                  </Space>
                ) : (
                  <Space size={8} align="center">
                    <Text style={{ color: token.colorText }}>
                      {oreLocal != null ? `${oreLocal} h` : '—'}
                    </Text>
                    {scartoEl}
                  </Space>
                )}
              </Descriptions.Item>

              {/* Copertura sinistro — modificabile solo con work_orders.edit
                  (AMMINISTRAZIONE/DIREZIONE), in sola lettura per tutti gli altri */}
              {hasSinistro && (
                <Descriptions.Item label="Sinistro">
                  {canEditSinistro ? (
                    <Spin spinning={isSavingSinistro} size="small">
                      <Checkbox
                        checked={intervention.coperto_sinistro ?? false}
                        disabled={isSavingSinistro}
                        onChange={e => onSaveSinistro(intervention.id!, e.target.checked)}
                      >
                        <SafetyOutlined style={{ marginRight: 6, color: token.colorWarning }} />
                        Intervento coperto da sinistro
                        {numeroSinistro && (
                          <span style={{ color: token.colorTextSecondary, marginLeft: 8, fontSize: 12 }}>
                            (N° {numeroSinistro})
                          </span>
                        )}
                      </Checkbox>
                    </Spin>
                  ) : (
                    <Text style={{ color: token.colorText }}>
                      {intervention.coperto_sinistro
                        ? 'Intervento coperto dal sinistro'
                        : 'Intervento non coperto dal sinistro'}
                      {numeroSinistro && (
                        <span style={{ color: token.colorTextSecondary, marginLeft: 8, fontSize: 12 }}>
                          (N° {numeroSinistro})
                        </span>
                      )}
                    </Text>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* ── Blocco RDA/RDO ─── */}
            <div style={{ marginTop: 8 }}>
              <BloccoRdaIntervento
                record={intervention}
                interventionRdaMap={interventionRdaMap}
                rdaChecks={rdaChecks}
                rdaDates={rdaDates}
                savingRda={savingRda}
                onToggleItemCheck={onToggleItemCheck}
                onDateChange={onDateChange}
                onSaveRdaReceive={onSaveRdaReceive}
              />
            </div>

            {/* ── Preventivi officina ─── */}
            <div style={{ marginTop: 8 }}>
              <BloccoPreventiviOfficina
                workOrderId={workOrderId}
                quotes={quotes}
                canViewQuotes={canViewQuotes}
                canApprove={canApprove}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Componente principale ─────────────────────────────────────────────────────
const ElencoInterventi: React.FC<Props> = ({ workOrder, onChange, onPhotoCaptureIntervention }) => {
  const { token } = theme.useToken()

  // Auth
  const { user } = useAuth()
  // NOTA: `ruolo` è il codice di display (vedi @/types: la fonte
  // autorevole è `ruolo_id`). Usato qui per coerenza con FaseAnalisi.
  // Ticket W36 aperto per allineare tutti i controlli su ruolo_id.
  const isWorkshopRole = user?.ruolo === 'WORKSHOP'

  // Permessi
  const canEdit         = usePermission('interventions.edit')
  const canDelete       = usePermission('interventions.delete')
  // La V2 (FaseAnalisi) esclude le officine esterne dalla cancellazione:
  // {!isWorkshopRole && canDeleteIntervention && ...}. Qui la stessa regola
  // viaggia nella prop, cosi' la card resta senza logica di ruolo.
  const canDeleteIntervento = canDelete && !isWorkshopRole
  const canCreateIntervento = usePermission('interventions.create')
  const canUpdateStatus = usePermission('interventions.update_status')
  const canEditOre      = canEdit || canUpdateStatus
  // Lo stato dell'intervento muove la macchina a stati della scheda: la V2
  // (FaseAnalisi) lo governa con work_orders.edit OR interventions.update_status,
  // che NON coincide con canEditOre (li' il primo termine e' interventions.edit).
  const canEditStato    = usePermission('work_orders.edit') || canUpdateStatus
  const canCreatePhotos = usePermission('work_order_photos.create')
  const canViewQuotes   = usePermission('workshop_quotes.view')
  const canApprove      = usePermission('workshop_quotes.approve')
  // Le officine esterne non assegnano ad altre officine: stessa regola della V2.
  const canAssign       = usePermission('workshop_quotes.request') && !isWorkshopRole
  // coperto_sinistro decide se l'intervento e' a carico del cliente o
  // dell'assicurazione: lo modifica solo chi gestisce amministrazione/direzione.
  const canEditSinistro = usePermission('work_orders.edit')

  // Lookup tabelle di sistema
  const { data: interventionStatuses = [] } = useInterventionStatusTypes()
  const { data: resolutionModes = [] } = useInterventionResolutionModes()

  // Mappa id → label per modalità di risoluzione
  const resolutionLabels: Record<number, string> = Object.fromEntries(
    resolutionModes.map(m => [m.id, m.nome])
  )

  // ── Preventivi officina ──────────────────────────────────────────────────
  // Una fetch per scheda, raggruppata per intervention_id — uguale a FaseAnalisi
  const { data: workshopQuotes = [] } = useWorkshopQuotes(workOrder.id, { enabled: canViewQuotes })
  const interventionQuoteMap = useMemo(() => {
    const map = new Map<number, WorkshopQuote[]>()
    for (const q of workshopQuotes) {
      const arr = map.get(q.intervention_id) ?? []
      arr.push(q)
      map.set(q.intervention_id, arr)
    }
    return map
  }, [workshopQuotes])

  // ── Stato RDA/RDO ──────────────────────────────────────────────────────────
  // rdaChecks: purchaseRequestId → itemId → checked
  const [rdaChecks, setRdaChecks] = useState<Record<number, Record<number, boolean>>>({})
  const [rdaDates, setRdaDates]   = useState<Record<number, Record<number, ReturnType<typeof dayjs> | null>>>({})
  const [savingRda, setSavingRda] = useState<Record<number, boolean>>({})

  // Sistema vecchio (rdas/rda_items) — una fetch per scheda, non per intervento
  const { data: workOrderRdas } = useRdasForWorkOrder(workOrder.id)
  const interventionRdaMap = useMemo(() => {
    const map: Record<number, { id: number; note?: string | null; items: { id: number; descrizione: string }[] }> = {}
    for (const rda of workOrderRdas ?? []) {
      if (rda.intervention_id) map[rda.intervention_id] = rda
    }
    return map
  }, [workOrderRdas])

  // Stato locale salvataggio
  const [savingStatoId, setSavingStatoId] = useState<number | null>(null)
  const [savingOreId, setSavingOreId]     = useState<number | null>(null)
  const [savingSinistroId, setSavingSinistroId] = useState<number | null>(null)
  const [deletingId, setDeletingId]       = useState<number | null>(null)

  // Modale nota obbligatoria
  const [notaModal, setNotaModal] = useState<NotaModal | null>(null)
  const [notaText, setNotaText]   = useState('')

  // Modale consenso avvertito (ripresa con RDA pendente)
  const [consensoModal, setConsensoModal] = useState<ConsensoModal | null>(null)
  const [consensoText, setConsensoText]   = useState('')

  // Modale assegna officina
  const [assegnaModal, setAssegnaModal] = useState<AssegnaModal | null>(null)

  // Modale creazione/modifica intervento
  const [interventoModalOpen, setInterventoModalOpen] = useState(false)
  const [editingIntervento, setEditingIntervento]     = useState<Intervention | null>(null)

  // Modale RDA post-creazione
  const [rdaAfterNew, setRdaAfterNew] = useState<RdaAfterNew>({
    open: false,
    interventionId: 0,
    descrizione: '',
    note: null,
  })

  // Interventi ordinati per progressivo
  const interventi = [...(workOrder.interventions ?? [])].sort(
    (a, b) => (a.progressivo ?? 0) - (b.progressivo ?? 0)
  )

  // ── Cambio stato ────────────────────────────────────────────────────────────
  const handleSaveStato = useCallback(async (
    interventionId: number,
    statoId: number,
    nota?: string,
    motivazione?: string,
  ): Promise<void> => {
    const status = interventionStatuses.find(s => s.id === statoId)
    if (status?.richiede_nota && !nota) {
      setNotaText('')
      setNotaModal({ interventionId, statoId })
      return
    }

    // Consenso avvertito: si sta riprendendo un intervento fermo perche' manca
    // il materiale, e la RDA e' ancora pendente. Prima di procedere l'operativo
    // deve sapere quale RDA sta scavalcando e scrivere perche' lo fa.
    const iv = workOrder.interventions?.find(i => i.id === interventionId)
    const statoPrima = interventionStatuses.find(s => s.id === iv?.stato_intervento_id)
    if (
      !motivazione &&
      statoPrima?.codice === 'sospeso' &&
      status?.codice !== 'sospeso' &&
      iv?.sospensione_origine === 'rda' &&
      iv?.rda_pendente
    ) {
      setConsensoText('')
      setConsensoModal({
        interventionId,
        statoId,
        rdaCodice: iv.rda_pendente.codice,
        rdaFornitore: iv.rda_pendente.fornitore ?? null,
      })
      return
    }

    setSavingStatoId(interventionId)
    try {
      await axiosInstance.patch(
        `/work-orders/${workOrder.id}/interventions/${interventionId}/status`,
        {
          stato_intervento_id: statoId,
          ...(nota ? { note_sospensione: nota } : {}),
          ...(motivazione ? { motivazione_ripresa: motivazione } : {}),
        },
      )
      onChange()
    } catch (err: any) {
      // Il backend spiega nel detail perche' rifiuta (es. il vincolo della RDA):
      // mostrarlo, invece di un generico che lascia l'utente al buio.
      message.error(err?.response?.data?.detail || 'Salvataggio stato fallito')
    } finally {
      setSavingStatoId(null)
    }
  }, [interventionStatuses, workOrder.id, workOrder.interventions, onChange])

  const handleNotaConfirm = useCallback(() => {
    if (!notaModal) return
    if (!notaText.trim()) { message.warning('Inserisci un motivo'); return }
    const { interventionId, statoId } = notaModal
    setNotaModal(null)
    handleSaveStato(interventionId, statoId, notaText.trim())
  }, [notaModal, notaText, handleSaveStato])

  const handleConsensoConfirm = useCallback(() => {
    if (!consensoModal) return
    if (!consensoText.trim()) { message.warning('Inserisci una motivazione'); return }
    const { interventionId, statoId } = consensoModal
    setConsensoModal(null)
    handleSaveStato(interventionId, statoId, undefined, consensoText.trim())
  }, [consensoModal, consensoText, handleSaveStato])

  // ── Salvataggio ore effettive ────────────────────────────────────────────────
  // Se l'utente ha interventions.edit: PUT (aggiorna qualunque campo).
  // Se ha solo interventions.update_status: PATCH /status con lo stato invariato
  // + ore_effettive. La note_sospensione esistente viene propagata per non
  // cancellarla e per soddisfare richiede_nota quando lo stato è sospeso.
  const handleSaveOre = useCallback(async (
    interventionId: number,
    ore: number | null,
  ): Promise<void> => {
    setSavingOreId(interventionId)
    try {
      if (canEdit) {
        await axiosInstance.put(
          `/work-orders/${workOrder.id}/interventions/${interventionId}`,
          { ore_effettive: ore },
        )
      } else {
        const iv = workOrder.interventions?.find(i => i.id === interventionId)
        const payload: Record<string, unknown> = {
          stato_intervento_id: iv?.stato_intervento_id,
          ore_effettive: ore,
        }
        if (iv?.note_sospensione) {
          payload.note_sospensione = iv.note_sospensione
        }
        await axiosInstance.patch(
          `/work-orders/${workOrder.id}/interventions/${interventionId}/status`,
          payload,
        )
      }
      onChange()
    } catch {
      message.error('Salvataggio ore effettive fallito')
    } finally {
      setSavingOreId(null)
    }
  }, [canEdit, workOrder.id, workOrder.interventions, onChange])

  // ── Salvataggio copertura sinistro ──────────────────────────────────────────
  // Stesso pattern delle ore effettive: PUT sull'intervento con il solo campo
  // cambiato. NON si usa PATCH /status (quello serve a chi ha solo update_status).
  const handleSaveSinistro = useCallback(async (
    interventionId: number,
    coperto: boolean,
  ): Promise<void> => {
    setSavingSinistroId(interventionId)
    try {
      await axiosInstance.put(
        `/work-orders/${workOrder.id}/interventions/${interventionId}`,
        { coperto_sinistro: coperto },
      )
      onChange()
    } catch {
      message.error('Salvataggio copertura sinistro fallito')
    } finally {
      setSavingSinistroId(null)
    }
  }, [workOrder.id, onChange])

  // ── Creazione / modifica intervento ──────────────────────────────────────────
  const handleInterventoConfirm = useCallback(async (data: NuovoInterventoData) => {
    try {
      if (editingIntervento) {
        await axiosInstance.put(
          `/work-orders/${workOrder.id}/interventions/${editingIntervento.id}`,
          data,
        )
        onChange()
        setInterventoModalOpen(false)
        setEditingIntervento(null)
      } else {
        const res = await axiosInstance.post(
          `/work-orders/${workOrder.id}/interventions`,
          data,
        )
        onChange()
        setInterventoModalOpen(false)
        setEditingIntervento(null)
        const newId = res.data?.id
        if (newId) {
          Modal.confirm({
            title: 'Intervento salvato',
            content: 'Vuoi gestire subito la Richiesta di Acquisto?',
            okText: 'Sì, gestisci RDA',
            cancelText: 'No, chiudi',
            onOk: () => setRdaAfterNew({
              open: true,
              interventionId: newId,
              descrizione: data.descrizione_intervento,
              note: data.note_intervento,
            }),
          })
        }
      }
    } catch {
      message.error('Salvataggio intervento fallito')
    }
  }, [editingIntervento, workOrder.id, onChange])

  // ── Eliminazione intervento ──────────────────────────────────────────────────
  const handleDeleteIntervento = useCallback(async (interventionId: number) => {
    setDeletingId(interventionId)
    try {
      await axiosInstance.delete(
        `/work-orders/${workOrder.id}/interventions/${interventionId}`,
      )
      onChange()
    } catch {
      message.error('Eliminazione fallita')
    } finally {
      setDeletingId(null)
    }
  }, [workOrder.id, onChange])

  // ── Apri modifica ────────────────────────────────────────────────────────────
  const handleEdit = useCallback((intervention: Intervention) => {
    setEditingIntervento(intervention)
    setInterventoModalOpen(true)
  }, [])

  // ── Ricezione articoli RDO ───────────────────────────────────────────────────
  const toggleItemCheck = useCallback((prId: number, itemId: number, checked: boolean) => {
    setRdaChecks(prev => ({
      ...prev,
      [prId]: { ...(prev[prId] ?? {}), [itemId]: checked },
    }))
    if (checked) {
      setRdaDates(prev => ({
        ...prev,
        [prId]: { ...(prev[prId] ?? {}), [itemId]: dayjs() },
      }))
    }
  }, [])

  const handleSaveRdaReceive = useCallback(async (pr: any) => {
    const itemChecks = rdaChecks[pr.id] ?? {}
    const dates = rdaDates[pr.id] ?? {}
    const items: any[] = pr.items ?? []
    const newlyChecked = items.filter((item: any) => !item.ricevuto && itemChecks[item.id])
    if (newlyChecked.length === 0) return

    const missingDate = newlyChecked.some((item: any) => !dates[item.id])
    if (missingDate) {
      message.warning('Imposta la data di ricezione per tutti gli articoli spuntati')
      return
    }

    setSavingRda(prev => ({ ...prev, [pr.id]: true }))
    try {
      for (const item of newlyChecked) {
        await axiosInstance.patch(
          `/purchase-requests/${pr.id}/items/${item.id}/receive`,
          { data_ricezione_item: dates[item.id]!.toISOString() },
        )
      }
      onChange()
      message.success('Ricezione registrata')
    } catch {
      message.error('Errore nella registrazione della ricezione')
    } finally {
      setSavingRda(prev => ({ ...prev, [pr.id]: false }))
    }
  }, [rdaChecks, rdaDates, onChange])

  // ── Apri modale officina ─────────────────────────────────────────────────────
  const handleAssegnaClick = useCallback((
    interventionId: number,
    currentWorkshopId: number | null,
  ) => {
    setAssegnaModal({ interventionId, currentWorkshopId })
  }, [])

  // ── Pulsante "+ Intervento" ───────────────────────────────────────────────────
  const btnNuovoIntervento = !isWorkshopRole && canCreateIntervento ? (
    <Button
      size="small"
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => { setEditingIntervento(null); setInterventoModalOpen(true) }}
    >
      Intervento
    </Button>
  ) : null

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <Card
        size="small"
        title={
          <Space>
            <ToolOutlined />
            <Text strong>Interventi</Text>
            <Badge count={interventi.length} color={token.colorPrimary} />
          </Space>
        }
        extra={btnNuovoIntervento}
        style={{
          background: token.colorBgContainer,
          borderColor: token.colorBorder,
          marginTop: 12,
        }}
        styles={{ body: { paddingTop: 8 } }}
      >
        {interventi.length === 0 ? (
          <Text type="secondary">Nessun intervento registrato.</Text>
        ) : (
          interventi.map(iv => (
            <CardIntervento
              key={iv.id}
              intervention={iv}
              workOrderId={workOrder.id!}
              interventionStatuses={interventionStatuses}
              resolutionLabels={resolutionLabels}
              onSaveStato={handleSaveStato}
              onSaveOre={handleSaveOre}
              onAssegnaClick={handleAssegnaClick}
              onEdit={handleEdit}
              onDelete={handleDeleteIntervento}
              onPhotoCaptureIntervention={onPhotoCaptureIntervention}
              savingStatoId={savingStatoId}
              savingOreId={savingOreId}
              deletingId={deletingId}
              canEditOre={canEditOre}
              canEditStato={canEditStato}
              canEdit={canEdit}
              canDelete={canDeleteIntervento}
              canCreatePhotos={canCreatePhotos}
              canAssign={canAssign}
              hasSinistro={!!workOrder.sinistro}
              numeroSinistro={workOrder.numero_sinistro}
              canEditSinistro={canEditSinistro}
              onSaveSinistro={handleSaveSinistro}
              savingSinistroId={savingSinistroId}
              quotes={interventionQuoteMap.get(iv.id ?? 0) ?? []}
              canViewQuotes={canViewQuotes}
              canApprove={canApprove}
              interventionRdaMap={interventionRdaMap}
              rdaChecks={rdaChecks}
              rdaDates={rdaDates}
              savingRda={savingRda}
              onToggleItemCheck={toggleItemCheck}
              onDateChange={(prId, itemId, date) =>
                setRdaDates(prev => ({
                  ...prev,
                  [prId]: { ...(prev[prId] ?? {}), [itemId]: date },
                }))
              }
              onSaveRdaReceive={handleSaveRdaReceive}
            />
          ))
        )}
      </Card>

      {/* ── Modale nota obbligatoria ─── */}
      <Modal
        title="Motivo richiesto"
        open={!!notaModal}
        onOk={handleNotaConfirm}
        onCancel={() => setNotaModal(null)}
        okText="Conferma"
        cancelText="Annulla"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Questo stato richiede una nota (es. motivo sospensione).
        </Text>
        <textarea
          value={notaText}
          onChange={e => setNotaText(e.target.value)}
          rows={3}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: token.borderRadius,
            border: `1px solid ${token.colorBorder}`,
            background: token.colorBgContainer,
            color: token.colorText,
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: 14,
          }}
          placeholder="Inserisci un motivo…"
        />
      </Modal>

      {/* ── Modale consenso avvertito: ripresa con RDA pendente ─── */}
      <Modal
        title="Intervento in attesa di materiali"
        open={!!consensoModal}
        onOk={handleConsensoConfirm}
        onCancel={() => setConsensoModal(null)}
        okText="Riprendi comunque"
        cancelText="Annulla"
        okButtonProps={{ danger: true, disabled: !consensoText.trim() }}
      >
        <Text style={{ display: 'block', marginBottom: 8, color: token.colorText }}>
          Questo intervento è fermo in attesa di materiali.
        </Text>
        <Text strong style={{ display: 'block', marginBottom: 8, color: token.colorWarningText }}>
          RDA {consensoModal?.rdaCodice}
          {consensoModal?.rdaFornitore ? ` — ${consensoModal.rdaFornitore}` : ''}, approvata e non ancora ricevuta.
        </Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Riprendendolo scavalchi questo vincolo. Scrivi perché: la motivazione resta
          registrata nello storico della scheda.
        </Text>
        <textarea
          value={consensoText}
          onChange={e => setConsensoText(e.target.value)}
          rows={3}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: token.borderRadius,
            border: `1px solid ${token.colorBorder}`,
            background: token.colorBgContainer,
            color: token.colorText,
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: 14,
          }}
          placeholder="Motivo della ripresa…"
        />
      </Modal>

      {/* ── Modale assegna officina ─── */}
      {assegnaModal && (
        <AssegnaOfficinaModal
          interventionId={assegnaModal.interventionId}
          workOrderId={workOrder.id!}
          currentWorkshopId={assegnaModal.currentWorkshopId}
          open={!!assegnaModal}
          onClose={() => setAssegnaModal(null)}
          onSuccess={() => { setAssegnaModal(null); onChange() }}
          interventions={workOrder.interventions}
        />
      )}

      {/* ── Modale creazione/modifica intervento ─── */}
      <NuovoInterventoModal
        open={interventoModalOpen}
        onClose={() => { setInterventoModalOpen(false); setEditingIntervento(null) }}
        onConfirm={handleInterventoConfirm}
        initialData={editingIntervento ? {
          descrizione_intervento: editingIntervento.descrizione_intervento,
          modalita_risoluzione_id: editingIntervento.modalita_risoluzione_id ?? 1,
          tipo_intervento: editingIntervento.tipo_intervento,
          durata_stimata: editingIntervento.durata_stimata,
          note_intervento: editingIntervento.note_intervento ?? null,
          ore_effettive: editingIntervento.ore_effettive ?? null,
          coperto_sinistro: editingIntervento.coperto_sinistro ?? false,
        } : undefined}
        interventionId={editingIntervento?.id}
        workOrderId={workOrder.id}
        workOrder={workOrder}
        canEditSinistro={canEditSinistro}
      />

      {/* ── Modale RDA post-creazione ─── */}
      {rdaAfterNew.interventionId > 0 && (
        <RdaInterventoModal
          open={rdaAfterNew.open}
          onClose={() => setRdaAfterNew({ open: false, interventionId: 0, descrizione: '', note: null })}
          interventionId={rdaAfterNew.interventionId}
          workOrderId={workOrder.id!}
          interventionInfo={{
            descrizione: rdaAfterNew.descrizione,
            note: rdaAfterNew.note,
          }}
        />
      )}
    </>
  )
}

export default ElencoInterventi
